import { ReactNode, forwardRef } from 'react';

import { InfiniteScroll } from '../../InfiniteScroll';
import { CircularProgress } from '../../CircularProgress';
import { Box } from '../../Box';

import useStyles from './ListBox.styles';

/**
 * Свойства компонента {@link ListBox}
 */
interface ListBoxProps {
  /**
   * Флаг, указывающий, есть ли еще элементы для загрузки.
   */
  hasMore: boolean;

  /**
   * Текущее количество элементов в списке.
   */
  dataLength: number;

  /**
   * Функция, вызываемая для загрузки следующей порции элементов.
   */
  next: () => void;

  /**
   * Флаг, указывающий, что данные загружаются.
   */
  loading: boolean;

  /**
   * Дочерние элементы списка.
   */
  children?: ReactNode | ReactNode[];
}

/**
 * Компонент ListBox представляет собой контейнер для списков с бесконечной прокруткой.
 * Он использует компонент InfiniteScroll для загрузки дополнительных элементов при прокрутке.
 *
 * @internal
 * @param hasMore - Флаг, указывающий, есть ли еще элементы для загрузки.
 * @param dataLength - Текущее количество элементов в списке.
 * @param next - Функция, вызываемая для загрузки следующей порции элементов.
 * @param loading - Флаг, указывающий, что данные загружаются.
 * @param children - Дочерние элементы списка.
 * @returns Компонент ListBox.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link ListBoxProps}
 */
export const ListBox = forwardRef<HTMLDivElement, ListBoxProps>(
  ({ hasMore, dataLength, next, loading, children, ...props }, ref) => {
    const { classes } = useStyles();

    return (
      <div
        {...props}
        id="multiple-autocomplete-input-scrollable"
        ref={ref}
        className={classes.scrollableWrapper}
      >
        <InfiniteScroll
          next={next}
          dataLength={dataLength}
          hasMore={hasMore}
          loader={
            loading && (
              <Box display="flex" justifyContent="center" p="16">
                <CircularProgress size={30} />
              </Box>
            )
          }
          endMessage={<div />}
          scrollId="multiple-autocomplete-input-scrollable"
        >
          <ul className={classes.listBox}>{children}</ul>
        </InfiniteScroll>
      </div>
    );
  },
);
